import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  getAgents,
  getAgentReconciliation,
  getCarriers,
  getCarrierReconciliation,
  downloadAgentReconciliationExcel,
  downloadCarrierReconciliationExcel,
} from "../../api/agents";
import {
  getContrabusSalesReconciliation,
  downloadContrabusSalesReconciliationExcel,
} from "../../api/partners";
import { isPlatformAdminUser, isTenantAdminUser } from "../../utils/auth";
import DownloadIcon from "@mui/icons-material/Download";
import i18n from "../../i18n";

const formatUah = (value) => {
  const amount = Number(value || 0).toLocaleString("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return i18n.t("units.uahAmount", { amount });
};

const formatMoney = (value, currency) => {
  const amount = Number(value || 0).toLocaleString("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!currency || String(currency).toUpperCase() === "UAH") {
    return i18n.t("units.uahAmount", { amount });
  }
  return `${amount} ${currency}`;
};

const fundsHolderLabel = (holder, t) => {
  if (holder === "agent") return t("admin.fundsHolderAgent");
  if (holder === "carrier") return t("admin.fundsHolderCarrier");
  return t("admin.fundsHolderSv");
};

// Partner inventory sales stay in the API. The tab returns when we sell those tickets.
const SHOW_CONTRABUS_SALES = false;

const AgentReconciliationPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [mode, setMode] = useState("agent");
  const [agents, setAgents] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [entityId, setEntityId] = useState("");
  const [monthValue, setMonthValue] = useState(dayjs().format("YYYY-MM"));
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState("");
  const [settlementFilter, setSettlementFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  const isAgentMode = mode === "agent";
  const isContrabusMode = mode === "contrabus";
  const isPlatform = isPlatformAdminUser();

  useEffect(() => {
    if (!isPlatform && !isTenantAdminUser()) {
      navigate("/403", { replace: true });
    }
  }, [isPlatform, navigate]);

  const loadLists = useCallback(async () => {
    setListLoading(true);
    setError("");
    try {
      const [agentsData, carriersData] = await Promise.all([
        getAgents(),
        getCarriers(),
      ]);
      const agentsList = Array.isArray(agentsData)
        ? agentsData
        : agentsData.results || [];
      const carriersList = Array.isArray(carriersData)
        ? carriersData
        : carriersData.results || [];
      setAgents(agentsList);
      setCarriers(carriersList);
    } catch (e) {
      setError(t("admin.reconciliationLoadError"));
    } finally {
      setListLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    setReport(null);
    setSettlementFilter("all");
    if (mode === "contrabus") {
      setEntityId("");
      return;
    }
    const list = mode === "agent" ? agents : carriers;
    if (list.length) {
      setEntityId(String(list[0].id));
    } else {
      setEntityId("");
    }
  }, [mode, agents, carriers]);

  const handleModeChange = (_event, next) => {
    if (next) setMode(next);
  };

  const handleRun = async () => {
    if (!monthValue) return;
    if (!isContrabusMode && !entityId) return;
    const [yearStr, monthStr] = monthValue.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    setLoadingReport(true);
    setError("");
    setReport(null);
    try {
      let data;
      if (isContrabusMode) {
        data = await getContrabusSalesReconciliation({ year, month });
      } else if (isAgentMode) {
        data = await getAgentReconciliation(entityId, { year, month });
      } else {
        data = await getCarrierReconciliation(entityId, { year, month });
      }
      setReport(data);
    } catch (e) {
      setError(t("admin.reconciliationLoadError"));
    } finally {
      setLoadingReport(false);
    }
  };

  const handleExport = async () => {
    if (!monthValue) return;
    if (!isContrabusMode && !entityId) return;
    const [yearStr, monthStr] = monthValue.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    setExporting(true);
    setError("");
    try {
      let response;
      let prefix = "zvirka_carrier";
      if (isContrabusMode) {
        response = await downloadContrabusSalesReconciliationExcel({
          year,
          month,
        });
        prefix = "zvirka_contrabus";
      } else if (isAgentMode) {
        response = await downloadAgentReconciliationExcel(entityId, {
          year,
          month,
        });
        prefix = "zvirka_agent";
      } else {
        response = await downloadCarrierReconciliationExcel(entityId, {
          year,
          month,
        });
      }
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${prefix}_${year}-${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(t("admin.reconciliationExportError"));
    } finally {
      setExporting(false);
    }
  };

  const entities = isAgentMode ? agents : carriers;
  const entityLabel = isAgentMode ? t("admin.agent") : t("admin.carrier");
  const emptyListKey = isAgentMode
    ? "admin.reconciliationNoAgents"
    : "admin.reconciliationNoCarriers";
  const emptyRowsKey = isContrabusMode
    ? "admin.reconciliationEmptyContrabus"
    : isAgentMode
      ? "admin.reconciliationEmpty"
      : "admin.reconciliationEmptyCarrier";

  const reportEntity = isAgentMode ? report?.agent : report?.carrier;
  const percentLabel = isAgentMode
    ? t("admin.commissionPercent")
    : t("admin.retainPercent");
  const percentValue = isAgentMode
    ? reportEntity?.commission_percent
    : reportEntity?.retain_percent;

  const rate = report?.exchange_rate;
  const rows = (report?.rows || []).filter((row) => {
    if (settlementFilter === "all") return true;
    if (isContrabusMode) return row.funds_direction === settlementFilter;
    if (isAgentMode) return row.settlement === settlementFilter;
    return true;
  });

  const canRun = isContrabusMode ? Boolean(monthValue) : Boolean(entityId && monthValue);
  const showEntityPicker = !isContrabusMode;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <MuiLink
        component={RouterLink}
        to="/administrator/dashboard"
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackIcon fontSize="inherit" />
        {t("admin.dashboardTitle")}
      </MuiLink>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t("admin.reconciliationTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("admin.reconciliationHint")}
      </Typography>

      <Box sx={{ maxWidth: "100%", overflowX: "auto", mb: 3 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={handleModeChange}
          sx={{ flexWrap: "nowrap" }}
        >
          <ToggleButton value="agent">{t("admin.reconciliationModeAgent")}</ToggleButton>
          <ToggleButton value="carrier">
            {t("admin.reconciliationModeCarrier")}
          </ToggleButton>
          {SHOW_CONTRABUS_SALES ? (
            <ToggleButton value="contrabus">
              {t("admin.reconciliationModeContrabus")}
            </ToggleButton>
          ) : null}
        </ToggleButtonGroup>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        {listLoading && showEntityPicker ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : showEntityPicker && entities.length === 0 ? (
          <Typography color="text.secondary">{t(emptyListKey)}</Typography>
        ) : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            {showEntityPicker ? (
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel id="entity-select-label">{entityLabel}</InputLabel>
                <Select
                  labelId="entity-select-label"
                  label={entityLabel}
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                >
                  {entities.map((item) => (
                    <MenuItem key={item.id} value={String(item.id)}>
                      {item.name} (
                      {isAgentMode
                        ? `${item.commission_percent}%`
                        : `${item.retain_percent}%`}
                      )
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <TextField
              size="small"
              type="month"
              label={t("admin.reconciliationMonth")}
              InputLabelProps={{ shrink: true }}
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <Button
              variant="contained"
              onClick={handleRun}
              disabled={!canRun || loadingReport}
              sx={{ borderRadius: 2 }}
            >
              {loadingReport ? t("admin.loading") : t("admin.runReconciliation")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!canRun || exporting || loadingReport}
              sx={{ borderRadius: 2 }}
            >
              {exporting ? t("admin.loading") : t("admin.exportExcel")}
            </Button>
          </Stack>
        )}
      </Paper>

      {report ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1}
            mb={2}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {isContrabusMode
                  ? t("admin.reconciliationModeContrabus")
                  : reportEntity?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("admin.reconciliationPeriod", {
                  month: String(report.month).padStart(2, "0"),
                  year: report.year,
                })}
                {" · "}
                {isContrabusMode ? (
                  <>
                    {t("admin.commissionPercent")}: {report.commission_percent}%
                  </>
                ) : (
                  <>
                    {percentLabel}: {percentValue}%
                  </>
                )}
                {rate?.rate ? (
                  <>
                    {" · "}
                    {t("admin.systemExchangeRate", {
                      rate: rate.rate,
                      currency: rate.currency || "EUR",
                    })}
                  </>
                ) : null}
              </Typography>
            </Box>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {isContrabusMode ? t("admin.bookings") : t("admin.passengers")}
                </Typography>
                <Typography fontWeight={700}>
                  {isContrabusMode
                    ? (report.totals?.bookings ?? 0)
                    : (report.totals?.passengers ?? 0)}
                </Typography>
              </Box>
              {isContrabusMode ? (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.contrabusRetail")}
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatUah(report.totals?.retail_uah)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.dueToContrabus")}
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatUah(report.totals?.due_to_contrabus_uah)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.dueFromContrabus")}
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatUah(report.totals?.due_from_contrabus_uah)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.netSettlementContrabus")}
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatUah(report.totals?.net_uah)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.contrabusMargin")}
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatUah(report.totals?.margin_uah)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.ticketTotal")}
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatUah(report.totals?.ticket_uah)}
                    </Typography>
                  </Box>
                  {isAgentMode ? (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.dueToAgent")}
                        </Typography>
                        <Typography fontWeight={700}>
                          {formatUah(report.totals?.due_to_agent_uah)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.dueFromAgent")}
                        </Typography>
                        <Typography fontWeight={700}>
                          {formatUah(report.totals?.due_from_agent_uah)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.netSettlement")}
                        </Typography>
                        <Typography fontWeight={700}>
                          {formatUah(report.totals?.net_uah)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.retainTotal")}
                        </Typography>
                        <Typography fontWeight={700}>
                          {formatUah(report.totals?.retain_uah)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.payoutToCarrier")}
                        </Typography>
                        <Typography fontWeight={700}>
                          {formatUah(report.totals?.payout_uah)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </>
              )}
            </Stack>
          </Stack>

          {isAgentMode || isContrabusMode ? (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={settlementFilter}
              onChange={(_e, v) => v && setSettlementFilter(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="all">{t("admin.settlementFilterAll")}</ToggleButton>
              {isContrabusMode ? (
                <>
                  <ToggleButton value="to_contrabus">
                    {t("admin.dueToContrabus")}
                  </ToggleButton>
                  <ToggleButton value="from_contrabus">
                    {t("admin.dueFromContrabus")}
                  </ToggleButton>
                </>
              ) : (
                <>
                  <ToggleButton value="due_to_agent">
                    {t("admin.dueToAgent")}
                  </ToggleButton>
                  <ToggleButton value="due_from_agent">
                    {t("admin.dueFromAgent")}
                  </ToggleButton>
                </>
              )}
            </ToggleButtonGroup>
          ) : null}

          {rows.length === 0 ? (
            <Typography color="text.secondary">{t(emptyRowsKey)}</Typography>
          ) : isContrabusMode ? (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("admin.tripDate")}</TableCell>
                    <TableCell>{t("admin.number")}</TableCell>
                    <TableCell>{t("admin.route")}</TableCell>
                    <TableCell>{t("admin.carrier")}</TableCell>
                    <TableCell>{t("admin.paymentColumn")}</TableCell>
                    <TableCell>{t("admin.status")}</TableCell>
                    <TableCell align="right">{t("admin.contrabusRetail")}</TableCell>
                    <TableCell align="right">{t("admin.contrabusPartner")}</TableCell>
                    <TableCell align="right">
                      {t("admin.commissionPercent")}
                    </TableCell>
                    <TableCell align="right">{t("admin.settlementAmount")}</TableCell>
                    <TableCell align="right">{t("admin.contrabusMargin")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.booking_number}>
                      <TableCell>{row.trip_date}</TableCell>
                      <TableCell>
                        <MuiLink
                          component={RouterLink}
                          to={`/administrator/bookings/${row.booking_number}`}
                          underline="hover"
                        >
                          {row.booking_number}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{row.route}</TableCell>
                      <TableCell>{row.carrier || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            row.payment_method === "card"
                              ? t("admin.paymentMethodCard")
                              : t("admin.paymentMethodOnSite")
                          }
                          color={
                            row.funds_direction === "from_contrabus"
                              ? "warning"
                              : "success"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.status_display || row.status}</TableCell>
                      <TableCell align="right">
                        {formatUah(row.retail_uah)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatMoney(row.partner_price, row.partner_currency)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatUah(row.partner_uah)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.commission_percent}%
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {formatUah(row.commission_uah)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatUah(
                            row.funds_direction === "from_contrabus"
                              ? row.due_from_contrabus_uah
                              : row.due_to_contrabus_uah
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.funds_direction === "from_contrabus"
                            ? t("admin.dueFromContrabus")
                            : t("admin.dueToContrabus")}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatUah(row.margin_uah)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("admin.tripDate")}</TableCell>
                    <TableCell>{t("admin.number")}</TableCell>
                    <TableCell>{t("admin.passenger")}</TableCell>
                    <TableCell>{t("admin.route")}</TableCell>
                    {isAgentMode ? (
                      <TableCell>{t("admin.fundsHolder")}</TableCell>
                    ) : (
                      <TableCell>{t("admin.agent")}</TableCell>
                    )}
                    <TableCell>{t("admin.status")}</TableCell>
                    <TableCell align="right">{t("admin.ticketPrice")}</TableCell>
                    {isAgentMode ? (
                      <>
                        <TableCell align="right">
                          {t("admin.commissionPercent")}
                        </TableCell>
                        <TableCell align="right">
                          {t("admin.settlementAmount")}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right">
                          {t("admin.retainAmount")}
                        </TableCell>
                        <TableCell align="right">
                          {t("admin.payoutToCarrier")}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={`${row.booking_number}-${row.passenger_id}`}>
                      <TableCell>{row.trip_date}</TableCell>
                      <TableCell>
                        <MuiLink
                          component={RouterLink}
                          to={`/administrator/bookings/${row.booking_number}`}
                          underline="hover"
                        >
                          {row.booking_number}
                        </MuiLink>
                      </TableCell>
                      <TableCell>
                        {row.surname} {row.name}
                        {row.ticket_amount != null &&
                        row.ticket_currency &&
                        String(row.ticket_currency).toUpperCase() !== "UAH" ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {formatMoney(row.ticket_amount, row.ticket_currency)}
                          </Typography>
                        ) : row.partner_price != null &&
                          row.partner_currency &&
                          String(row.partner_currency).toUpperCase() !== "UAH" ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {formatMoney(row.partner_price, row.partner_currency)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.route}</TableCell>
                      {isAgentMode ? (
                        <TableCell>
                          <Chip
                            size="small"
                            label={fundsHolderLabel(row.funds_holder, t)}
                            color={
                              row.settlement === "due_from_agent"
                                ? "warning"
                                : "success"
                            }
                            variant="outlined"
                          />
                        </TableCell>
                      ) : (
                        <TableCell>{row.agent_name || "—"}</TableCell>
                      )}
                      <TableCell>{row.status_display || row.status}</TableCell>
                      <TableCell align="right">
                        {formatUah(row.ticket_uah)}
                      </TableCell>
                      {isAgentMode ? (
                        <>
                          <TableCell align="right">
                            {row.commission_percent}%
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatMoney(
                                row.settlement_amount ?? row.settlement_uah,
                                row.ticket_currency || "UAH"
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.settlement === "due_from_agent"
                                ? t("admin.dueFromAgent")
                                : t("admin.dueToAgent")}
                              {": "}
                              {formatUah(row.settlement_uah ?? row.payout_uah)}
                            </Typography>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="right">
                            {formatUah(row.retain_uah)}
                          </TableCell>
                          <TableCell align="right">
                            {formatUah(row.payout_uah)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      ) : null}
    </Container>
  );
};

export default AgentReconciliationPage;
